import { Box, Typography, useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FoodsIcon, WhitePopover } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { NumericalScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";

interface Props {
    appearance?: "legacy" | "product-v2";
    specificityRate?: NumericalScoreValue | undefined;
    isExtendedInfo?: boolean;
}

const SpecificityRateDisplay = (props: Props) => {
    const { appearance = "legacy", specificityRate, isExtendedInfo = false } = props;

    const theme = useTheme();
    const { t } = useTranslation();

    const tooltip = (
        <Box padding={theme.spacing(1)} textAlign="left">
            <Typography variant="body">
                {t(
                    "scorecard.specificity_rate.tooltip",
                    "Shows how much of the available data has been completed. Higher means more accurate results.",
                )}
            </Typography>
        </Box>
    );

    const isProductV2 = appearance === "product-v2";
    const icon =
        isProductV2 ?
            <Box component="span" sx={{ color: theme.palette.text.secondary, display: "inline-flex" }}>
                <FoodsIcon icon="information" size={2} />
            </Box>
        :   <InfoOutlinedIcon fontSize="small" color="action" />;

    if (specificityRate === undefined || specificityRate === null) return null;

    return (
        <Box
            alignItems="center"
            data-testid="specificity-rate"
            display="inline-flex"
            gap={isProductV2 ? theme.spacing(0.5) : 0.75}
            mb={isProductV2 ? undefined : 1}
            mt={isProductV2 ? undefined : 2}
            px={isProductV2 ? theme.spacing(1) : 0.5}
            py={isProductV2 ? theme.spacing(0.5) : 0.1}
            sx={{
                backgroundColor: isProductV2 ? theme.palette.background.paper : theme.palette.grey[100],
                borderColor: isProductV2 ? theme.custom.strokes.containerDivider.color : theme.palette.grey[300],
                borderRadius: isProductV2 ? `${theme.shape.borderRadius}px` : theme.spacing(0.5),
                borderStyle: isProductV2 ? theme.custom.strokes.containerDivider.style : "solid",
                borderWidth: isProductV2 ? theme.custom.strokes.containerDivider.width : theme.spacing(0.25),
                color: theme.palette.text.primary,
                cursor: "default",
                maxWidth: "100%",
                whiteSpace: isProductV2 && !isExtendedInfo ? "nowrap" : "normal",
                width: "fit-content",
            }}
        >
            {isExtendedInfo && (
                <>
                    <WhitePopover icon={icon} body={tooltip} size={theme.spacing(30)} />
                    <Typography variant="body-medium">
                        {t(
                            "scorecard.specificity_rate.extended_info",
                            "{{genericRate}}% of the recorded data are generic data. This has an impact on the specificity of the score.",
                            {
                                genericRate: 100 - specificityRate,
                            },
                        )}
                    </Typography>
                </>
            )}
            {!isExtendedInfo &&
                (isProductV2 ?
                    <>
                        <Typography component="span" variant="small-bold">
                            {t("scorecard.specificity_rate.rate", "Specificity rate data")}: {specificityRate} %
                        </Typography>
                        <WhitePopover icon={icon} body={tooltip} size={theme.spacing(30)} />
                    </>
                :   <>
                        <Typography variant="body-bold" sx={{ fontSize: theme.spacing(1.6) }}>
                            {t("scorecard.specificity_rate.rate", "Specificity rate data")}:
                        </Typography>
                        <Typography variant="body-medium" sx={{ fontSize: theme.spacing(1.6) }}>
                            {specificityRate}%
                        </Typography>
                        <WhitePopover icon={icon} body={tooltip} size={theme.spacing(30)} />
                    </>)}
        </Box>
    );
};

export default SpecificityRateDisplay;
